import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { motion } from 'framer-motion';
import { BiTime, BiSave, BiReset } from 'react-icons/bi';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';

const Configuration = ({ user }) => {
  const [callSettings, setCallSettings] = useState({
    startHour: 8,
    startMinute: 45,
    endHour: 16,
    endMinute: 45,
    redialeWaiting: '30m',
    redialMode: 'Calling mode', 
    redialMaxDays: '1 day'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch calling hours on mount
  useEffect(() => {
    if (!user?.id) {
      setError('User not authenticated. Please log in.');
      toast.error('User not authenticated. Please log in.');
      return;
    }

    setIsLoading(true);
    axios.get(`${process.env.REACT_APP_SERVER_API}/api/settings/calling-hours?userId=${user.id}`)
      .then(response => {
        if (response.data.callSettings) {
          setCallSettings(response.data.callSettings);
        }
      })
      .catch(err => {
        console.error('Error fetching calling hours:', err);
        setError('Failed to load calling hours. Using default settings.');
        toast.error('Failed to load calling hours. Using default settings.');
      })
      .finally(() => setIsLoading(false));
  }, [user?.id]);

  // Handle form submission
  const handleSaveSettings = async () => {
    if (!user?.id) {
      toast.error('User not authenticated');
      setError('User not authenticated');
      return;
    }

    // Validate time range
    const startTime = new Date();
    startTime.setHours(callSettings.startHour, callSettings.startMinute);
    const endTime = new Date();
    endTime.setHours(callSettings.endHour, callSettings.endMinute);
    if (endTime <= startTime) {
      toast.error('End time must be after start time');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_SERVER_API}/api/settings/calling-hours`, {
        userId: user.id,
        ...callSettings
      });
      console.log('Save settings response:', response.data); // Debugging log
    //   if (response.data.callSettings && typeof response.data.callSettings === 'object') {
        setCallSettings(response.data.callSettings);

setTimeout(()=>{
    toast.success('Calling hours updated successfully');
},2000)

       
        setError(null);
    
    } catch (error) {
      console.error('Error saving calling hours:', error);
      toast.error(error.response?.data?.error || 'Failed to update calling hours');
      setError('Failed to update calling hours');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle reset to default
  const handleReset = () => {
    setCallSettings({
      startHour: 8,
      startMinute: 45,
      endHour: 16,
      endMinute: 45,
      redialeWaiting: '30m',
      redialMode: '',
      redialMaxDays: '1 day'
    });
    toast.info('Reset to default calling hours');
    setError(null);
  };

  // Format time for display
  const formatTime = ({ hour, minute }) => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const formattedHour = hour % 12 || 12;
    return `${formattedHour}:${minute.toString().padStart(2, '0')} ${period}`;
  };

  // Render loading or error state
  if (isLoading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-gray-50 flex items-center justify-center">
        <AiOutlineLoading3Quarters className="animate-spin text-blue-600" size={48} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-gray-50 p-6 lg:p-8">
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md p-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-8 flex items-center gap-2">
            <BiTime className="text-purple-600" /> Calling Configuration
          </h1>
          <p className="text-red-600">{error}</p>
        </div>
        <ToastContainer position="top-right" autoClose={3000} />
      </div>
    );
  }

  return (
    <motion.div
      className="min-h-[calc(100vh-4rem)] bg-gray-50 p-6 lg:p-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-8 flex items-center gap-2">
          <BiTime className="text-purple-600" /> Calling Configuration
        </h1>

        <div className="bg-white rounded-xl shadow-md p-6">
          {/* Current Settings Display */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">Current Calling Hours</h2>
            <div className="flex items-center gap-4 text-gray-600">
              <p>
                <span className="font-medium">Start:</span> {formatTime({ hour: callSettings.startHour, minute: callSettings.startMinute })}
              </p>
              <p>
                <span className="font-medium">End:</span> {formatTime({ hour: callSettings.endHour, minute: callSettings.endMinute })}
              </p>
            </div>
          </div>

          {/* Settings Form */}
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
              <div className="flex gap-4">
                <select
                  value={callSettings.startHour}
                  onChange={(e) => setCallSettings({ ...callSettings, startHour: parseInt(e.target.value) })}
                  className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500"
                  disabled={isLoading}
                >
                  {[...Array(24)].map((_, i) => (
                    <option key={i} value={i}>{i.toString().padStart(2, '0')}</option>
                  ))}
                </select>
                <select
                  value={callSettings.startMinute}
                  onChange={(e) => setCallSettings({ ...callSettings, startMinute: parseInt(e.target.value) })}
                  className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500"
                  disabled={isLoading}
                >
                  {[0, 15, 30, 45].map(min => (
                    <option key={min} value={min}>{min.toString().padStart(2, '0')}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
              <div className="flex gap-4">
                <select
                  value={callSettings.endHour}
                  onChange={(e) => setCallSettings({ ...callSettings, endHour: parseInt(e.target.value) })}
                  className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500"
                  disabled={isLoading}
                >
                  {[...Array(24)].map((_, i) => (
                    <option key={i} value={i}>{i.toString().padStart(2, '0')}</option>
                  ))}
                </select>
                <select
                  value={callSettings.endMinute}
                  onChange={(e) => setCallSettings({ ...callSettings, endMinute: parseInt(e.target.value) })}
                  className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500"
                  disabled={isLoading}
                >
                  {[0, 15, 30, 45].map(min => (
                    <option key={min} value={min}>{min.toString().padStart(2, '0')}</option>
                  ))}
                </select>
              </div>

            </div>
          

{/*  */}



            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rediale Waiting List</label>
              <select
                value={callSettings.redialeWaiting}
                onChange={(e) => setCallSettings({ ...callSettings, redialeWaiting: e.target.value })}
                className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500"
                disabled={isLoading}
              >
                {['30m', '1hr', '2hr', '3hr', '4hr', '5hr'].map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Calling Mode</label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="redialMode"
                    value="Auto Calling"
                    checked={callSettings.redialMode === 'Auto Calling'}
                    onChange={(e) => setCallSettings({ ...callSettings, redialMode: e.target.value })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    disabled={isLoading}
                  />
                  <span className="text-gray-700">Auto Calling</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="redialMode"
                    value="Manual Call"
                    checked={callSettings.redialMode === 'Manual Call'}
                    onChange={(e) => setCallSettings({ ...callSettings, redialMode: e.target.value })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    disabled={isLoading}
                  />
                  <span className="text-gray-700">Manual Call</span>
                </label>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Redial Upto Max Days</label>
              <select
                value={callSettings.redialMaxDays}
                onChange={(e) => setCallSettings({ ...callSettings, redialMaxDays: e.target.value })}
                className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500"
                disabled={isLoading}
              >
                {['1 day', '2 day', '3 day'].map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>



            </div>
          {/* </div> */}

          {/* Action Buttons */}
          <div className="flex justify-end gap-4 mt-8">
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 flex items-center gap-2"
              disabled={isLoading}
            >
              <BiReset /> Reset to Default
            </button>
            <button
              onClick={handleSaveSettings}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
              disabled={isLoading}
            >
              {isLoading ? (
                <AiOutlineLoading3Quarters className="animate-spin" />
              ) : (
                <BiSave />
              )}
              Save Changes
            </button>
          </div>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </motion.div>
  );
};

export default Configuration;